import { useGetOrdersList } from "@birdwatch/client";

import type { PlaceOrderStatus } from "@/features/office/places/detail/main/orders/_hooks/use-place-order-statuses";
import {
  buildPlaceOrderStatusQuery,
  buildPlacePastOrdersQuery,
} from "@/features/office/places/detail/main/orders/_libs/place-order-query";

/**
 * Orders for a place (including drafts) via REST v5 — used to pick a linkable
 * order. Sorted by status ascending, status relation included for the status
 * icon.
 */
export const useOrdersByPlaceId = (
  placeId: string | undefined,
  options?: { enabled?: boolean },
) => {
  return useGetOrdersList()
    .filters({
      placeId: placeId || undefined,
      showDraft: "true",
    })
    .sort("status")
    .includes("status")
    .take(-1)
    .get({ enabled: options?.enabled ?? !!placeId });
};

/**
 * One active status group of the place "Orders" tab: its own paginated page of
 * orders plus the exact total from `withCount` (falling back to the pages
 * loaded so far while the count is absent).
 */
export const usePlaceOrderStatusGroup = (
  placeId: string,
  status: PlaceOrderStatus,
) => {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
    buildPlaceOrderStatusQuery(useGetOrdersList(), {
      placeId,
      status: { id: status.id, statusKey: String(status.statusKey) },
    }).getInfinite();

  const orders = data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages[0]?.extra?.total ?? orders.length;

  return {
    orders,
    total,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
};

/**
 * The single "Past orders" group of the place "Orders" tab (Done / Cancelled /
 * Deferred), newest update first.
 */
export const usePlacePastOrders = (placeId: string, statusKeys: string[]) => {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
    buildPlacePastOrdersQuery(useGetOrdersList(), {
      placeId,
      statusKeys,
    }).getInfinite();

  const orders = data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages[0]?.extra?.total ?? orders.length;

  return {
    orders,
    total,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
};
